"use client";
import {
  Box,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";
import {useEffect, useRef} from "react";
import {usePathname} from "next/navigation";
import {BiChevronRight, BiChevronDown} from "@onemind-services-llc/react-icons-ng/bi";
import {NAV_ITEMS, NavItem} from "../header.constants";

const NavItemWithPopover = ({item}: {item: NavItem}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  const pathname = usePathname();
  const navigationStarted = useRef(false);

  useEffect(() => {
    if (navigationStarted.current) {
      onClose();
      navigationStarted.current = false;
    }
  }, [pathname, onClose]);

  const handleClose = () => {
    if (!navigationStarted.current) {
      onClose();
    }
  };

  const handleLinkClick = () => {
    navigationStarted.current = true;
  };

  const linkColor = useColorModeValue("gray.600", "gray.200");
  const linkHoverColor = useColorModeValue("blue.400", "white");
  const popoverContentBgColor = useColorModeValue("white", "gray.800");

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={handleClose}
        trigger="hover"
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Box
            p={2}
            fontWeight={500}
            fontSize="18px"
            color={isOpen ? linkHoverColor : linkColor}
            _hover={{textDecoration: "none", color: linkHoverColor}}
            cursor="pointer"
          >
            <Link href={item.href ?? "#"} onClick={handleLinkClick}>
              <Flex textAlign="center" alignItems="center" gap={2}>
                {item.icon && <Icon as={item.icon} boxSize={8} />}
                {item.image && (
                  <Image src={item.image} alt={item.label} width={20} height={20} />
                )}
                {item.label}
                {item.children && (
                  <Icon
                    as={BiChevronDown}
                    transition="all .25s ease-in-out"
                    w={5}
                    h={5}
                  />
                )}
              </Flex>
            </Link>
          </Box>
        </PopoverTrigger>

        {item.children && (
          <PopoverContent
            border="1px solid #eee"
            boxShadow="sm"
            bg={popoverContentBgColor}
            p="10px"
            rounded="xl"
            minW="370px"
            fontSize="18px"
          >
            <Stack>
              {item.children.map((child) => (
                <DesktopSubNav
                  key={child.label}
                  {...child}
                  parentOnClose={handleClose}
                  onClick={handleLinkClick}
                />
              ))}
            </Stack>
          </PopoverContent>
        )}
      </Popover>
    </Box>
  );
};

export const DesktopNav = () => {
  return (
    <Stack direction="row" spacing={4} align="center">
      {NAV_ITEMS.map((item) => (
        <NavItemWithPopover key={item.label} item={item} />
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({
  icon,
  image,
  label,
  href,
  children,
  parentOnClose,
  onClick,
}: NavItem & {parentOnClose: () => void; onClick: () => void}) => {
  const hasChildren = children && children.length > 0;
  const popoverContentBgColor = useColorModeValue("white", "gray.800");

  const handleClick = () => {
    onClick();
  };

  return (
    <Popover trigger="hover" placement="right-start">
      <PopoverTrigger>
        <Box
          role="group"
          display="block"
          borderLeft="1px solid #eee"
          p={2}
          rounded="md"
          _hover={{bg: useColorModeValue("blue.50", "gray.900")}}
          cursor="pointer"
        >
          <Link href={href ?? "#"} onClick={handleClick}>
            <Stack direction="row" align="center">
              <HStack spacing={4}>
                {icon && <Icon as={icon} boxSize={6} />}
                {image && <Image src={image} alt={label} width={20} height={20} />}
                <Text
                  transition="all .3s ease"
                  _groupHover={{color: "blue.400"}}
                  fontWeight={500}
                >
                  {label}
                </Text>
              </HStack>
              {hasChildren && (
                <Flex
                  transition="all .3s ease"
                  transform="translateX(-10px)"
                  _groupHover={{color: "blue.400"}}
                  justify="flex-end"
                  align="center"
                  flex={1}
                >
                  <Icon as={BiChevronRight} w={5} h={5} />
                </Flex>
              )}
            </Stack>
          </Link>
        </Box>
      </PopoverTrigger>

      {children &&
        (children.length > 16 ? (
          <PopoverContent
            border="1px solid #eee"
            boxShadow="xl"
            bg={popoverContentBgColor}
            p="10px"
            rounded="xl"
            minW="640px"
          >
            <SimpleGrid columns={{base: 1, sm: 2}} spacing={2}>
              {children.map((child) => (
                <DesktopSubNav
                  key={child.label}
                  {...child}
                  parentOnClose={parentOnClose}
                  onClick={onClick}
                />
              ))}
            </SimpleGrid>
          </PopoverContent>
        ) : (
          <PopoverContent
            border="1px solid #eee"
            boxShadow="xl"
            bg={popoverContentBgColor}
            p="10px"
            rounded="xl"
            minW="370px"
          >
            <Stack>
              {children.map((child) => (
                <DesktopSubNav
                  key={child.label}
                  {...child}
                  parentOnClose={parentOnClose}
                  onClick={onClick}
                />
              ))}
            </Stack>
          </PopoverContent>
        ))}
    </Popover>
  );
};
